/* eslint-disable @typescript-eslint/no-unused-vars */
import notify from '../../../utils/notify';

import { APIRoutes } from './routes';
import { constructEndpointUrl } from '../../../lib/constructEndpointUrl';
import type { AgentDetails, Sessions, TeamDetails } from '../../../types/os';
import useWorkspaceStore from '../../../store/workspace/workspaceStore';
import { authorizedApiClient } from '../../../services/config';

function buildHeaders(): HeadersInit {
  // Pull workspace id from store or localStorage fallback
  const wsId =
    (typeof window !== 'undefined' && useWorkspaceStore.getState()?.workspace?.id) ||
    (typeof window !== 'undefined' ? localStorage.getItem('he_defws') : null);

  const headers: Record<string, string> = {};
  if (wsId) headers['X-Workspace-Id'] = wsId;
  return headers;
}

export const getAgentsAPI = async (endpoint: string): Promise<AgentDetails[]> => {
  const base = constructEndpointUrl(endpoint);
  const url = APIRoutes.GetAgents(base);
  try {
    const response = await authorizedApiClient.get(url, {
      headers: buildHeaders() as any,
      withCredentials: true,
      validateStatus: () => true,
    });
    if (response.status < 200 || response.status >= 300) {
      notify({
        type: 'failure',
        messageList: [`Failed to fetch  agents: ${response.statusText || response.status}`],
      });
      return [];
    }
    return response.data as AgentDetails[];
  } catch {
    notify({
      type: 'failure',
      messageList: ['Error fetching  agents'],
    });
    return [];
  }
};

export const getStatusAPI = async (base: string): Promise<number> => {
  const url = APIRoutes.Status(constructEndpointUrl(base));
  const response = await authorizedApiClient.get(url, {
    headers: buildHeaders() as any,
    withCredentials: true,
    validateStatus: () => true,
  });
  return response.status;
};

export const getAllSessionsAPI = async (
  base: string,
  type: 'agent' | 'team',
  componentId: string,
  dbId: string
): Promise<Sessions | { data: [] }> => {
  try {
    const url = new URL(APIRoutes.GetSessions(constructEndpointUrl(base)));
    url.searchParams.set('type', type);

    const response = await authorizedApiClient.get(url.toString(), {
      headers: buildHeaders() as any,
      withCredentials: true,
      validateStatus: () => true,
    });

    if (response.status < 200 || response.status >= 300) {
      if (response.status === 404) {
        return { data: [] } as any;
      }
      throw new Error(`Failed to fetch sessions: ${response.statusText || response.status}`);
    }
    return response.data as Sessions;
  } catch {
    return { data: [] } as any;
  }
};

export const getSessionAPI = async (
  base: string,
  type: 'agent' | 'team',
  sessionId: string,
  dbId?: string
) => {
  const urlBase = constructEndpointUrl(base);
  const queryParams = new URLSearchParams({ type });
  if (dbId) queryParams.append('db_id', dbId);

  const response = await authorizedApiClient.get(
    `${APIRoutes.GetSession(urlBase, sessionId)}?${queryParams.toString()}`,
    {
      headers: buildHeaders() as any,
      withCredentials: true,
      validateStatus: () => true,
    }
  );

  if (response.status < 200 || response.status >= 300) {
    throw new Error(`Failed to fetch session: ${response.statusText || response.status}`);
  }

  return response.data;
};

export const deleteSessionAPI = async (
  base: string,
  dbId: string,
  sessionId: string
) => {
  const urlBase = constructEndpointUrl(base);
  const queryParams = new URLSearchParams();
  if (dbId) queryParams.append('db_id', dbId);
  const response = await authorizedApiClient.delete(
    `${APIRoutes.DeleteSession(urlBase, sessionId)}?${queryParams.toString()}`,
    {
      headers: buildHeaders() as any,
      withCredentials: true,
      validateStatus: () => true,
    }
  );
  return response as any;
};

export const getTeamsAPI = async (endpoint: string): Promise<TeamDetails[]> => {
  const base = constructEndpointUrl(endpoint);
  const url = APIRoutes.GetTeams(base);
  try {
    const response = await authorizedApiClient.get(url, {
      headers: buildHeaders() as any,
      withCredentials: true,
      validateStatus: () => true,
    });
    if (response.status < 200 || response.status >= 300) {
      notify({
        type: 'failure',
        messageList: [`Failed to fetch  teams: ${response.statusText || response.status}`],
      });
      return [];
    }
    return response.data as TeamDetails[];
  } catch {
    notify({
      type: 'failure',
      messageList: ['Error fetching  teams'],
    });
    return [];
  }
};

export const deleteTeamSessionAPI = async (
  base: string,
  teamId: string,
  sessionId: string
) => {
  const urlBase = constructEndpointUrl(base);
  const response = await authorizedApiClient.delete(
    APIRoutes.DeleteTeamSession(urlBase, teamId, sessionId),
    {
      headers: buildHeaders() as any,
      withCredentials: true,
      validateStatus: () => true,
    }
  );

  if (response.status < 200 || response.status >= 300) {
    throw new Error(`Failed to delete team session: ${response.statusText || response.status}`);
  }
  return response as any;
};

/**
 * Rename a session via Agno's built-in rename endpoint.
 */
export const renameSessionAPI = async (
  base: string,
  sessionId: string,
  sessionName: string,
  type: 'agent' | 'team' = 'agent',
  dbId?: string
) => {
  const urlBase = constructEndpointUrl(base);
  const queryParams = new URLSearchParams({ type });
  if (dbId) queryParams.append('db_id', dbId);

  const response = await authorizedApiClient.post(
    `${APIRoutes.RenameSession(urlBase, sessionId)}?${queryParams.toString()}`,
    { session_name: sessionName },
    {
      headers: buildHeaders() as any,
      withCredentials: true,
      validateStatus: () => true,
    }
  );

  if (response.status < 200 || response.status >= 300) {
    throw new Error(`Failed to rename session: ${response.statusText || response.status}`);
  }
  return response.data;
};

/**
 * Generate an AI-powered session title via the backend LLM endpoint.
 */
export const generateSessionTitleAPI = async (
  message: string,
  aiResponse?: string
): Promise<string> => {
  try {
    const response = await authorizedApiClient.post(
      '/ai/generate-session-title',
      {
        message,
        ai_response: aiResponse,
      },
      {
        headers: buildHeaders() as any,
        validateStatus: () => true,
      }
    );

    if (response.status >= 200 && response.status < 300 && response.data?.title) {
      return response.data.title;
    }
    return '';
  } catch {
    return '';
  }
};
